/* ==========================================================================
 * bb-winkelwagen.js - de brug tussen de rekenkern en de Shopify-winkel
 * --------------------------------------------------------------------------
 * GEDEELD door de keuzehulp en de paneelhulp. Alles wat met de winkel te maken
 * heeft staat hier, zodat bb-kern.js zuiver rekenwerk blijft en de twee
 * schermbestanden zuiver scherm.
 *
 * Hiervoor stonden er twee van deze bestanden in het thema: bb-winkelwagen.js
 * bij de paneelhulp en bb-kh-winkelwagen.js bij de keuzehulp. Ze deden
 * hetzelfde werk in een andere verpakking (gewoon script tegen ES-module) en
 * met andere namen. Dit is die twee samengevoegd, zonder dat er aan het gedrag
 * iets veranderd is. Waar de twee andere namen voor dezelfde handeling
 * gebruikten, staat de tweede naam er als alias bij; dat staat hieronder per
 * geval opgeschreven.
 *
 * WAAROM EEN GEWOON SCRIPT EN GEEN ES-MODULE
 * Zelfde afweging als bij bb-kern.js, en die staat uitgeschreven in
 * gedeeld/gereedschap/bouw-kern.mjs. Kort: een gewoon script hoeft zijn eigen
 * adres op het Shopify-cdn niet te kennen, het werkt op elk thema, en de
 * toetsen kunnen het in node in een sandbox draaien precies zoals de browser
 * het krijgt. Een thema kan gewone scripts en modules allebei aan; de volgorde
 * klopt zolang dit bestand met defer boven het module-blok van de keuzehulp
 * staat, want defer-scripts en module-scripts draaien allebei na het inlezen
 * van de pagina, in de volgorde waarin ze in de html staan.
 *
 * Vier taken:
 *   1. het JSON-blok van snippets/bb-rekendata.liquid inlezen en opschonen
 *   2. de producten in de VERPLICHTE volgorde zetten
 *   3. lezen wat er al in de winkelwagen ligt (/cart.js)
 *   4. een heel pakket in een keer toevoegen (/cart/add.js)
 *
 * WAAROM DE VOLGORDE ZO BELANGRIJK IS
 * De rekenkern leidt "meters per rol" af uit het EERSTE artikel van een
 * diameter, en kiest bij twee even dure pakketten het artikel dat het eerst in
 * de lijst staat. In WooCommerce kwam de lijst gesorteerd binnen. Op Shopify
 * hangt de volgorde aan de collectie-instelling, en die kan iemand per ongeluk
 * omzetten. Daarom sorteren we hier alsnog: oplopend op diameter, daarbinnen
 * oplopend op lengte. Zo kan de uitkomst nooit stil van de oude site af lopen
 * doordat er in de beheeromgeving iets versleept is.
 *
 * Er zit hier bewust geen DOM in behalve leesGegevens(), en geen rekenregel.
 * ========================================================================== */
(function (global) {
	'use strict';

	/**
	 * De basis waar /cart.js en /cart/add.js onder hangen.
	 *
	 * Shopify.routes.root is '/' of '/nl/' op een winkel met taalpaden. Staat
	 * het object er niet (oud thema, of dit bestand draait in een toets), dan
	 * is '/' de juiste terugval.
	 */
	function wortel() {
		return (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
	}

	/* ----------------------------------------------------------------------
	 * 1 EN 2. DE GEGEVENS
	 * -------------------------------------------------------------------- */

	/**
	 * Oplopend op diameter, daarbinnen oplopend op lengte. Bij gelijke lengte
	 * op variant-nummer, puur zodat de volgorde bij twee identieke artikelen
	 * niet per paginabezoek verspringt.
	 */
	function sorteerProducten(producten) {
		return producten.slice().sort(function (a, b) {
			if (a.diameter !== b.diameter) { return a.diameter - b.diameter; }
			if (a.meters !== b.meters) { return a.meters - b.meters; }
			return (a.id || 0) - (b.id || 0);
		});
	}

	/**
	 * Een artikel doet alleen mee als diameter, meters en rollen alle drie
	 * gevuld zijn en groter dan nul. Dat is de tegenhanger van de controle in
	 * bb_kh_producten(): zonder die velden rekent de tool niet, en met een
	 * halve invulling zou hij stil verkeerd rekenen.
	 */
	function bruikbaar(p) {
		return !!p
			&& p.diameter > 0
			&& p.meters > 0
			&& p.rollen > 0
			&& p.centen >= 0
			&& p.id;
	}

	/**
	 * Leest het JSON-blok dat snippets/bb-rekendata.liquid op de pagina zet.
	 * Geeft null terug als het blok er niet is of stuk is; de aanroeper toont
	 * dan een melding in plaats van stilletjes niets te doen.
	 */
	function leesGegevens(blokId) {
		var blok = global.document && global.document.getElementById(blokId || 'bb-rekendata');
		if (!blok) { return null; }
		var rauw;
		try {
			rauw = JSON.parse(blok.textContent || blok.innerText || '{}');
		} catch (e) {
			return null;
		}
		return schoon(rauw);
	}

	/** Zet een rauw JSON-blok om in de G die de rekenkern verwacht. */
	function schoon(rauw) {
		if (!rauw || !rauw.producten || !rauw.producten.length) { return null; }

		var producten = sorteerProducten(rauw.producten.filter(bruikbaar).map(function (p) {
			return {
				id: p.id,
				naam: String(p.naam || ''),
				url: String(p.url || ''),
				sku: String(p.sku || ''),
				diameter: Number(p.diameter),
				meters: Number(p.meters),
				rollen: Number(p.rollen),
				rollen_per_pallet: Number(p.rollen_per_pallet || 0),
				centen: Math.round(Number(p.centen)),
				vancenten: Math.round(Number(p.vancenten || p.centen))
			};
		}));
		if (!producten.length) { return null; }

		return {
			producten: producten,
			pallet: rauw.pallet || {},
			treden: rauw.treden || { een: 0, twee: 0 },
			/* Wat er al in de winkelwagen ligt komt NA de eerste tekening binnen,
			   zie rollenInWinkelwagen(). Tot die tijd is dit leeg, en dat is beter
			   dan een getal dat straks blijkt te kloppen noch te veranderen. */
			winkelwagen: {}
		};
	}

	/* ----------------------------------------------------------------------
	 * 3. HET BLOK DAT NAAR /cart/add.js GAAT
	 * -------------------------------------------------------------------- */

	/**
	 * LET OP: op Shopify koop je een VARIANT, niet een product. Het veld heet
	 * in de Liquid-JSON gewoon 'id' en bevat het variant-nummer; variant_id
	 * staat er als reservekopie in voor het geval de gegevens ooit uit een
	 * andere bron komen. Dezelfde volgorde als kern.cartAddPayload().
	 *
	 * @param {object} pakket    { regels: [{ product, aantal }] }
	 * @param {string} [secties] Komma-lijst met sectienamen die Shopify meteen
	 *                           mee mag renderen, bijvoorbeeld
	 *                           'cart-drawer,cart-icon-bubble'. Leeg = niets.
	 */
	function addPayload(pakket, secties) {
		var body = {
			items: pakket.regels.map(function (r) {
				return { id: r.product.variant_id || r.product.id, quantity: r.aantal };
			})
		};
		if (secties) { body.sections = secties; }
		return body;
	}

	/* ----------------------------------------------------------------------
	 * 4. WAT LIGT ER AL IN DE WINKELWAGEN
	 * -------------------------------------------------------------------- */

	/**
	 * Vertaalt de regels uit /cart.js naar rollen per diameter. Puur, geen
	 * fetch: zo is hij los na te rekenen.
	 *
	 * /cart.js geeft GEEN metafields terug, alleen variant-nummer, sku en
	 * aantal. De vertaling naar diameter en rollen komt dus uit dezelfde JSON
	 * die de tool al heeft. Gevolg: een rol die buiten de ingestelde collectie
	 * valt telt niet mee voor de pallet. In WooCommerce telde alles met
	 * _bb_rollen mee. Zet de collectie dus goed.
	 *
	 * @param {array}  producten  de lijst uit de Liquid-JSON
	 * @param {object} wagen      het antwoord van /cart.js
	 * @returns {object}          { "12": 4, "20": 6 }
	 */
	function rollenPerDiameter(producten, wagen) {
		var perVariant = {};
		(producten || []).forEach(function (p) { perVariant[String(p.variant_id || p.id)] = p; });

		var uit = {};
		var regels = (wagen && wagen.items) || [];
		regels.forEach(function (regel) {
			var p = perVariant[String(regel.id)];
			if (!p) { return; }                 // geen vogelwering van ons, telt niet mee
			uit[p.diameter] = (uit[p.diameter] || 0) + (p.rollen * regel.quantity);
		});
		return uit;
	}

	/** Rauwe inhoud van de winkelwagen. De belofte breekt bij een netwerkfout. */
	function winkelwagenLezen() {
		return fetch(wortel() + 'cart.js', {
			method: 'GET',
			headers: { Accept: 'application/json' },
			credentials: 'same-origin'
		}).then(function (antwoord) {
			if (!antwoord.ok) { throw new Error('cart.js gaf ' + antwoord.status); }
			return antwoord.json();
		});
	}

	/**
	 * Rollen per diameter die al in de winkelwagen liggen.
	 *
	 * Mislukt het lezen, dan komt er {} terug en geen fout: liever geen
	 * palletregel dan een palletregel met een verzonnen getal erin, en de
	 * palletregel mag de hele hulp niet omleggen.
	 */
	function rollenInWinkelwagen(producten) {
		return winkelwagenLezen()
			.then(function (wagen) { return rollenPerDiameter(producten, wagen); })
			.catch(function () { return {}; });
	}

	/* ----------------------------------------------------------------------
	 * 5. HET PAKKET IN DE WINKELWAGEN
	 * -------------------------------------------------------------------- */

	/**
	 * Zet een heel pakket in EEN verzoek in de winkelwagen.
	 *
	 * ALLES OF NIETS. Gaat er een variant mis (uitverkocht met voorraad-
	 * bewaking), dan geeft Shopify een 422 en is er NIETS toegevoegd, ook niet
	 * gedeeltelijk. Dat is anders dan WooCommerce, dat toevoegde wat kon en
	 * meldde hoeveel er gelukt was. De melding op het scherm zegt dat dus ook
	 * met zoveel woorden.
	 *
	 * @param {object} pakket   uitkomst van de rekenkern: { regels: [...] }
	 * @param {object|string} [opties]
	 *        De keuzehulp gaf hier { secties: '...' } door en de paneelhulp de
	 *        komma-lijst zelf. Allebei blijven werken; er hoefde dus aan geen
	 *        van beide aanroepen iets te veranderen.
	 * @returns {Promise<object>} { ok, aantalArtikelen, antwoord }
	 * @throws  {Error} met .status en .allesOfNiets = true als Shopify weigerde;
	 *          er is dan gegarandeerd NIETS toegevoegd. .uitslag en
	 *          .uitlegVanShopify staan er allebei op, want de twee tools keken
	 *          naar een andere naam.
	 */
	function pakketToevoegen(pakket, opties) {
		var secties = (typeof opties === 'string') ? opties : (opties && opties.secties);
		var body = addPayload(pakket, secties);

		return fetch(wortel() + 'cart/add.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
			credentials: 'same-origin',
			body: JSON.stringify(body)
		}).then(function (antwoord) {
			return antwoord.json().catch(function () { return {}; }).then(function (uitslag) {
				if (!antwoord.ok) {
					var uitleg = uitslag.description || uitslag.message || '';
					var fout = new Error(uitleg || ('cart/add.js gaf ' + antwoord.status));
					fout.allesOfNiets = true;
					fout.status = antwoord.status;
					fout.uitslag = uitslag;
					fout.uitlegVanShopify = uitleg;
					throw fout;
				}

				/* Het echte aantal uit het antwoord, niet uit ons eigen pakket:
				   als Shopify om wat voor reden dan ook minder heeft toegevoegd,
				   moet de melding dat zeggen en niet ons voorstel napraten. */
				var aantal = 0;
				var regels = uitslag.items || (uitslag.item ? [uitslag.item] : []);
				regels.forEach(function (r) { aantal += (r.quantity || 0); });

				return { ok: true, aantalArtikelen: aantal, antwoord: uitslag };
			});
		});
	}

	/**
	 * Vertelt de rest van het thema dat de winkelwagen veranderd is. Dawn,
	 * Refresh en de meeste andere thema's luisteren naar minstens een van
	 * deze. Kan geen kwaad als niemand luistert.
	 *
	 * Het wordt op documentElement afgevuurd en het borrelt op, dus een thema
	 * dat op document luistert hoort het net zo goed. Dat is met opzet de
	 * ruimste van de twee oude versies: de keuzehulp vuurde op documentElement,
	 * de paneelhulp op document.
	 *
	 * Geef hier het ANTWOORD VAN SHOPIFY door (uit.antwoord), niet het omhulsel
	 * dat pakketToevoegen teruggeeft. Een thema dat meeleest verwacht de
	 * winkelwageninhoud.
	 */
	function meldWijziging(antwoord) {
		if (typeof global.document === 'undefined' || !global.document) { return; }
		try {
			global.document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
				bubbles: true,
				detail: antwoord
			}));
			global.document.documentElement.dispatchEvent(new CustomEvent('cart:build', {
				bubbles: true,
				detail: antwoord
			}));
			if (global.jQuery) { global.jQuery(global.document.body).trigger('added_to_cart'); }
		} catch (e) { /* een thema dat niet luistert is geen fout */ }
	}

	global.BBWinkel = {
		wortel: wortel,
		sorteerProducten: sorteerProducten,
		bruikbaar: bruikbaar,
		schoon: schoon,
		leesGegevens: leesGegevens,
		addPayload: addPayload,
		rollenPerDiameter: rollenPerDiameter,
		winkelwagenLezen: winkelwagenLezen,
		rollenInWinkelwagen: rollenInWinkelwagen,
		pakketToevoegen: pakketToevoegen,
		meldWijziging: meldWijziging,

		/* Twee namen voor dezelfde handeling, uit de twee oude bestanden. Ze
		   staan hier zodat er aan de aanroepen in bb-keuzehulp.js niets
		   veranderd hoefde te worden. */
		routeWortel: wortel,
		themaBijwerken: meldWijziging
	};
}(typeof window !== 'undefined' ? window : globalThis));
